import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import {
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "@/store/redux";
import { logout, selectUser } from "@/store/redux/slices/authSlice";
import { ROUTES } from "@/routes/config";
import navItems from "./navItems";

const MainLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const headerHeight = 56;
  const userLabel = user?.name || user?.email || "Account";

  const handleLogout = () => {
    dispatch(logout());
    navigate(ROUTES.LOGIN);
  };

  const isNavItemActive = (itemPath: string) => {
    if (itemPath === ROUTES.DASHBOARD_HOME) {
      return (
        location.pathname === itemPath || location.pathname === ROUTES.DASHBOARD
      );
    }
    return location.pathname.startsWith(itemPath);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <AppBar position="fixed" elevation={0} sx={{ height: headerHeight }}>
        <Toolbar
          sx={{
            minHeight: headerHeight,
            height: headerHeight,
            px: { xs: 2, md: 3 },
            gap: 3,
          }}
        >
          {/* Brand */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              cursor: "pointer",
              flexShrink: 0,
            }}
            onClick={() => navigate(ROUTES.DASHBOARD_HOME)}
          >
            <Typography
              sx={{
                fontWeight: 800,
                color: "secondary.main",
                letterSpacing: "0.02em",
                fontSize: "1.05rem",
                lineHeight: 1,
              }}
            >
              VERVE VERO
            </Typography>
            <Box
              sx={{
                width: "1px",
                height: 22,
                bgcolor: "rgba(255,255,255,0.25)",
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "rgba(255,255,255,0.85)",
                letterSpacing: "0.01em",
                whiteSpace: "nowrap",
              }}
            >
              <FormattedMessage id="app.title" />
            </Typography>
          </Box>

          {/* Inline nav */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flex: 1,
              ml: 2,
              overflowX: "auto",
            }}
          >
            {navItems.map((item) => {
              const active = isNavItemActive(item.path);
              const Icon = item.icon;
              return (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  startIcon={<Icon fontSize="small" />}
                  sx={{
                    color: active ? "#ffffff" : "rgba(255,255,255,0.72)",
                    bgcolor: active ? "primary.main" : "transparent",
                    px: 1.5,
                    py: 0.5,
                    minHeight: 32,
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    borderRadius: 1,
                    whiteSpace: "nowrap",
                    "&:hover": {
                      bgcolor: active
                        ? "primary.dark"
                        : "rgba(255,255,255,0.1)",
                      color: "#ffffff",
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>

          {/* Right cluster */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Button
              onClick={(e) => setAnchorEl(e.currentTarget)}
              aria-label="Account menu"
              startIcon={<AccountCircleIcon />}
              endIcon={<ArrowDropDownIcon />}
              sx={{
                color: "rgba(255,255,255,0.9)",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                px: 1.25,
                py: 0.5,
                minHeight: 32,
                borderRadius: 1,
                maxWidth: 240,
                "& .MuiButton-startIcon": { mr: 0.75 },
                "& .MuiButton-endIcon": { ml: 0.25 },
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {userLabel}
              </Box>
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {user?.name || user?.email || ""}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email || ""}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  handleLogout();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          bgcolor: "background.default",
        }}
      >
        <Toolbar sx={{ minHeight: headerHeight }} />
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            bgcolor: "background.default",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
